/**
 * License Guard
 *
 * Blocks navigation to module-gated routes when the active team has no
 * active license for the required module(s). Use `licenseGuardFor(module)`
 * to compose into a route's `canActivate` chain after the auth / email /
 * profile guards (so the active team is loaded by the time this runs).
 *
 * Accepts a single module or a list. A list is OR semantics: one active
 * license among them is enough. That is what the Administration portal
 * needs, since it is a team-management surface that a Tools-only OR an
 * Academy-only partner is equally entitled to.
 *
 * The check always reads the *active* team, which on partner-scoped routes
 * (`/admin/:partnerId`, `/notebook/:partnerId`) has already been reconciled
 * to the URL's partner by `partnerAdminGuard` / `partnerMemberGuard`. So
 * this is a per-partner check, never a "any partner the user belongs to"
 * check.
 *
 * On denial the guard returns a `UrlTree` to `/license-expired?module=...`,
 * which is the canonical Angular pattern for "redirect, don't activate".
 * With a list, the first module is the one reported, since that page
 * explains a single module's never-had / expired / pending situation.
 *
 * @example
 * // Single module
 * {
 *   path: 'notebook',
 *   canActivate: [authGuard, emailVerifiedGuard, profileGuard, licenseGuardFor('tools')],
 * }
 *
 * @example
 * // Either module grants access
 * {
 *   path: 'admin/:partnerId',
 *   canActivate: [..., partnerAdminGuard, licenseGuardFor(['tools', 'academy'])],
 * }
 */

import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { LicenseModule } from 'src/app/core/schema';
import { AccountState } from 'src/app/store/account/account.state';

/**
 * Returns a CanActivateFn that gates the route on an active license for
 * `modules`. A list passes when *any* of the modules is active.
 */
export const licenseGuardFor = (modules: LicenseModule | LicenseModule[]): CanActivateFn => {
  const required: LicenseModule[] = Array.isArray(modules) ? modules : [modules];

  return () => {
    const accountState = inject(AccountState);
    const router = inject(Router);

    // No team selected, so send back to the team picker rather than the
    // license-expired page (the user has nothing to be expired against).
    if (!accountState.activeTeam()) {
      return router.parseUrl('/my-blume');
    }

    // Read off `activeTeamModules` rather than the per-module booleans so
    // this stays correct if a third module is ever added.
    const active = accountState.activeTeamModules();
    const hasAccess = required.some(module => active.has(module));

    if (!hasAccess) {
      return router.parseUrl(`/license-expired?module=${required[0]}`);
    }

    return true;
  };
};
